import React, {useCallback, useContext} from 'react';


import {ConfirmationModal} from 'src/components/confirmation-modal';
import {FormInput, NumberInput, PasswordInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {Skeleton} from 'src/components/skeleton';
import {CredentialsType as CredentialsTypes} from 'src/openapi/admin/models/CredentialsType';
import {CrmType as CrmTypes} from 'src/openapi/admin/models/CrmType';

import Button from '@pb/kit-component-button';
import FormSection from '@pb/kit-component-form-section';
import {Integer} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {RadioButton, RadioButtons} from '@pb/kit-component-radio-button';
import {LocaleContext} from '@pb/kit-context-locale';

import {EntitiesSection} from '../../components/entities-section';

import {MSDynamicsSectionSkeleton} from './ms-dynamics-section-skeleton';
import {isMsDynamicTestReady} from './utils/isMsDynamicTestReady';

import type {Dispatch, SetStateAction} from 'react';
import type {CredentialsType} from 'src/openapi/admin/models/CredentialsType';
import type {CrmAccountSettings} from 'src/openapi/admin/models/CrmAccountSettings';
import type {Error} from 'src/openapi/admin/models/Error';
import type {GetCrmLimit} from 'src/openapi/admin/models/GetCrmLimit';
import type {LinkageStatistics} from 'src/openapi/admin/models/LinkageStatistics';
import type {MsDynamicCredentials} from 'src/openapi/admin/models/MsDynamicCredentials';

type Props = {
    isLoading: boolean,
    isSyncLinkageLoading: boolean,
    isAuthRedirectUrlLoading: boolean,
    isLinkageStatisticLoading: boolean,
    isConnectionStatusLoading: boolean,
    linkageStatisticLoadingError: Error | null,
    disabled?: boolean,
    readonly?: boolean,

    limits: GetCrmLimit,
    credentials: MsDynamicCredentials,
    linkageStatistic: LinkageStatistics | null,
    settings: CrmAccountSettings,
    orgId?: string,

    onChangeLimits: (limits: GetCrmLimit) => void,
    onChangeCredentials: Dispatch<SetStateAction<MsDynamicCredentials>>,
    onSyncLinkageStatistic: () => void,
    onChangeSettings: Dispatch<SetStateAction<CrmAccountSettings>>,
    onSetupOAuthLogin: () => void,
    onSetupPasswordLogin: () => void,
    onRevokeToken: () => void,
};

export const MSDynamicsSection = (props: Props) => {
    const {
        isLoading,
        isSyncLinkageLoading,
        isAuthRedirectUrlLoading,
        isLinkageStatisticLoading,
        isConnectionStatusLoading,
        linkageStatisticLoadingError,
        disabled,
        readonly,

        limits,
        credentials,
        linkageStatistic,
        settings,
        orgId,

        onChangeLimits,
        onSyncLinkageStatistic,
        onChangeSettings,
        onChangeCredentials,
        onSetupOAuthLogin,
        onSetupPasswordLogin,
        onRevokeToken,
    } = props;
    const {locale} = useContext(LocaleContext);

    const changeMaxUsers = useCallback((maxUsers?: number) => {
        onChangeLimits({...limits, maxUsers: maxUsers || 0});
    }, [limits, onChangeLimits]);

    const changeMaxImports = useCallback((maxImports?: number) => {
        onChangeLimits({...limits, maxImports: maxImports || 0});
    }, [limits, onChangeLimits]);

    const changeImportCredits = useCallback((importCredits?: number) => {
        onChangeLimits({...limits, importCredits: importCredits || 0});
    }, [limits, onChangeLimits]);

    const changeCredentialsType = useCallback((credentialsType: CredentialsType) => {
        onChangeCredentials(prevState => ({...prevState, credentialsType}));
    }, [onChangeCredentials]);

    const changeLogin = useCallback((login: string) => {
        onChangeCredentials(prevState => ({
            ...prevState,
            passwordCredentials: {...prevState.passwordCredentials, login},
        }));
    }, [onChangeCredentials]);

    const changePassword = useCallback((password: string) => {
        onChangeCredentials(prevState => {
            const passwordCredentials = password ? {
                ...prevState.passwordCredentials,
                password,
                passwordChanged: true,
                passwordNotEmpty: true,
            } : prevState.passwordCredentials;

            return {...prevState, passwordCredentials};
        });
    }, [onChangeCredentials]);

    const clearPassword = useCallback(() => {
        onChangeCredentials(prevState => ({
            ...prevState,
            passwordCredentials: {
                ...prevState.passwordCredentials,
                passwordChanged: true,
                passwordNotEmpty: false,
                password: '',
            },
        }));
    }, [onChangeCredentials]);

    const changeURL = useCallback((url: string) => {
        onChangeCredentials(prevState => ({...prevState, url}));
    }, [onChangeCredentials]);

    const changeCrmURL = useCallback((crmUrl: string) => {
        onChangeCredentials(prevState => ({...prevState, crmUrl}));
    }, [onChangeCredentials]);

    return (
        <Skeleton shown={isLoading} view={<MSDynamicsSectionSkeleton/>}>
            <FormSection headline="Credentials">
                <Group direction="v" alignItems="start">
                    <Item>
                        <Label><RequiredMark label="Authentication type"/></Label>
                        <Item className="pt-s">
                            <RadioButtons
                                disabled={disabled || readonly}
                                name="AuthType"
                                value={credentials.credentialsType}
                                onChange={changeCredentialsType}>
                                <Group gap="l">
                                    <RadioButton
                                        showLabel={true}
                                        label="Password"
                                        value={CredentialsTypes.PASSWORD}
                                    />
                                    <RadioButton
                                        value={CredentialsTypes.OAUTH}
                                        label="OAuth"
                                    />
                                </Group>
                            </RadioButtons>
                        </Item>
                    </Item>
                    {credentials.credentialsType === CredentialsTypes.PASSWORD ? (
                        <Group direction="v" alignItems="start">
                            <ResponsiveElementGroup>
                                <FormInput
                                    width={300}
                                    label="Login"
                                    placeholder="MS Dynamics login"
                                    readOnly={readonly}
                                    disabled={disabled}
                                    value={credentials.passwordCredentials.login}
                                    onBlur={changeLogin}
                                />
                                <PasswordInput
                                    id="ms-dynamics-password"
                                    label="Password"
                                    width={300}
                                    readOnly={readonly}
                                    disabled={disabled}
                                    placeholder="MS Dynamics password"
                                    value={credentials.passwordCredentials.password}
                                    isNotEmpty={credentials.passwordCredentials.passwordNotEmpty}
                                    onBlur={changePassword}
                                    onClear={clearPassword}
                                />
                                <FormInput
                                    width={300}
                                    label="MS Dynamics URL"
                                    placeholder="MS Dynamics URL"
                                    readOnly={readonly}
                                    disabled={disabled}
                                    value={credentials.url}
                                    onBlur={changeURL}
                                />
                            </ResponsiveElementGroup>
                            <Button
                                disabled={disabled || readonly || !isMsDynamicTestReady(credentials)}
                                busy={isConnectionStatusLoading}
                                variant="Type.SECONDARY"
                                onClick={onSetupPasswordLogin}>
                                Save and Connect
                            </Button>
                        </Group>
                    ) : (
                        <Group direction="v">
                            <FormInput
                                width={300}
                                label="MS Dynamics URL"
                                placeholder="MS Dynamics URL"
                                readOnly={readonly}
                                disabled={disabled}
                                value={credentials.crmUrl}
                                onBlur={changeCrmURL}
                            />
                            <ResponsiveElementGroup>
                                <Button
                                    disabled={disabled || readonly || !credentials.crmUrl}
                                    busy={!!credentials.crmUrl && isAuthRedirectUrlLoading}
                                    variant="Type.SECONDARY"
                                    onClick={onSetupOAuthLogin}>
                                    Setup OAuth login
                                </Button>
                                <ConfirmationModal
                                    title="Revoke OAuth token"
                                    message="
                                    This will remove the connection between the PitchbBook Plugin and MS Dynamics.
                                    Are you sure you want to revoke th OAuth token?
                                "
                                    onConfirm={onRevokeToken}>
                                    <Button
                                        disabled={disabled || readonly}
                                        variant="Type.SECONDARY">
                                        Revoke OAuth token
                                    </Button>
                                </ConfirmationModal>
                            </ResponsiveElementGroup>
                        </Group>
                    )}
                </Group>
            </FormSection>
            <FormSection headline="CRM Limits">
                <ResponsiveElementGroup>
                    <NumberInput
                        width={300}
                        label={<RequiredMark label="MS Dynamics users"/>}
                        onBlur={changeMaxUsers}
                        readOnly={readonly}
                        placeholder="Max MS Dynamics users*"
                        disabled={disabled}
                        value={limits.maxUsers}
                        maxValue={2147483647}
                        displayedValue={Integer.format(limits.maxUsers, locale)}
                    />
                    <NumberInput
                        width={300}
                        label={<RequiredMark label="Imports & syncs"/>}
                        onBlur={changeMaxImports}
                        readOnly={readonly}
                        disabled={disabled}
                        placeholder="Max imports"
                        value={limits.maxImports || 0}
                        maxValue={2147483647}
                        displayedValue={Integer.format(limits.maxImports || 0, locale)}
                    />
                    <NumberInput
                        width={300}
                        label={<RequiredMark label="Import from PB"/>}
                        onBlur={changeImportCredits}
                        readOnly={readonly}
                        disabled={disabled}
                        placeholder="Import from PB"
                        value={limits.importCredits || 0}
                        maxValue={2147483647}
                        displayedValue={Integer.format(limits.importCredits || 0, locale)}
                    />
                </ResponsiveElementGroup>
            </FormSection>
            <EntitiesSection
                crmType={CrmTypes.MDCRM}
                disabled={disabled || readonly}
                isSyncLinkageLoading={isSyncLinkageLoading}
                isLinkageStatisticLoading={isLinkageStatisticLoading}
                linkageStatisticLoadingError={linkageStatisticLoadingError}
                settings={settings}
                orgId={orgId}
                linkageStatistic={linkageStatistic}
                onSyncLinkageStatistic={onSyncLinkageStatistic}
                onChangeSettings={onChangeSettings}
            />
        </Skeleton>
    );
};
